<script lang="ts">
  import type { SurfaceTask } from "./types";
  import { pinnedToday, markPinned, markUnpinned } from "./pinned";
  import { pinToToday, unpinFromToday } from "./api";
  import { longpress } from "./longpress";

  let {
    task,
    onerror,
    ondismiss,
    size = "md",
  }: {
    task: SurfaceTask;
    onerror?: (msg: string) => void;
    ondismiss?: (fp: string) => void;
    size?: "sm" | "md";
  } = $props();

  let menuOpen = $state(false);
  let busy = $state(false);

  const local = $derived($pinnedToday[task.fingerprint]);
  const isPinnedNow = $derived(
    !!local || !!task.pinned_today,
  );
  const isEveningNow = $derived(
    local ? local.evening : !!task.pinned_evening,
  );

  async function pin(evening: boolean) {
    if (busy) return;
    busy = true;
    markPinned(task.fingerprint, evening);
    try {
      await pinToToday({ fingerprint: task.fingerprint, evening });
    } catch (e) {
      markUnpinned(task.fingerprint);
      onerror?.(e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
  }

  async function unpin() {
    if (busy) return;
    busy = true;
    const prev = $pinnedToday[task.fingerprint];
    markUnpinned(task.fingerprint);
    try {
      await unpinFromToday({ fingerprint: task.fingerprint });
    } catch (e) {
      if (prev) markPinned(task.fingerprint, prev.evening, prev.day);
      onerror?.(e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
  }

  async function tap() {
    if (menuOpen) return;
    if (isPinnedNow) await unpin();
    else await pin(false);
  }

  function openMenu() {
    menuOpen = true;
  }

  function close() {
    menuOpen = false;
  }

  async function chooseToday() {
    close();
    await pin(false);
  }

  async function chooseEvening() {
    close();
    await pin(true);
  }

  async function chooseTomorrow() {
    close();
    const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
    const [y, m, d] = todayStr.split("-").map(Number);
    const tomorrow = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
    busy = true;
    markPinned(task.fingerprint, false, tomorrow);
    try {
      await pinToToday({ fingerprint: task.fingerprint, evening: false, day: tomorrow });
    } catch (e) {
      markUnpinned(task.fingerprint);
      onerror?.(e instanceof Error ? e.message : String(e));
    } finally {
      busy = false;
    }
    ondismiss?.(task.fingerprint);
  }

  async function chooseClear() {
    close();
    if (isPinnedNow) await unpin();
  }
</script>

<button
  type="button"
  class="star {size}"
  class:on={isPinnedNow}
  class:evening={isEveningNow}
  aria-label={isPinnedNow ? "Unpin from Today" : "Pin to Today"}
  disabled={busy}
  onclick={tap}
  use:longpress={{ onlong: openMenu }}
>
  {#if isEveningNow}
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M19.5 14.4a8.5 8.5 0 0 1-10-10 1 1 0 0 0-1.4-1A9.5 9.5 0 1 0 20.4 15.7a1 1 0 0 0-.9-1.3Z" fill="currentColor"/>
    </svg>
  {:else if isPinnedNow}
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M11.1 3.6a1 1 0 0 1 1.8 0l2.3 4.7 5.2.7a1 1 0 0 1 .55 1.7l-3.8 3.7.9 5.2a1 1 0 0 1-1.45 1.05L12 18l-4.6 2.6a1 1 0 0 1-1.45-1.05l.9-5.2-3.8-3.7a1 1 0 0 1 .55-1.7l5.2-.7Z" fill="currentColor"/>
    </svg>
  {:else}
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M11.1 3.6a1 1 0 0 1 1.8 0l2.3 4.7 5.2.7a1 1 0 0 1 .55 1.7l-3.8 3.7.9 5.2a1 1 0 0 1-1.45 1.05L12 18l-4.6 2.6a1 1 0 0 1-1.45-1.05l.9-5.2-3.8-3.7a1 1 0 0 1 .55-1.7l5.2-.7ZM12 6.6 10.4 9.9a1 1 0 0 1-.74.55l-3.6.5 2.6 2.55a1 1 0 0 1 .29.88l-.62 3.6 3.18-1.8a1 1 0 0 1 1 0l3.18 1.8-.62-3.6a1 1 0 0 1 .29-.88l2.6-2.55-3.6-.5a1 1 0 0 1-.74-.55Z" fill="currentColor"/>
    </svg>
  {/if}
</button>

{#if menuOpen}
  <div class="menu-backdrop" role="presentation" onclick={close} onkeydown={(e) => e.key === "Escape" && close()}>
    <div class="action-sheet" role="menu">
      <div class="action-group">
        <button type="button" class="action" onclick={chooseToday}>
          <span class="action-icon star-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M11.1 3.6a1 1 0 0 1 1.8 0l2.3 4.7 5.2.7a1 1 0 0 1 .55 1.7l-3.8 3.7.9 5.2a1 1 0 0 1-1.45 1.05L12 18l-4.6 2.6a1 1 0 0 1-1.45-1.05l.9-5.2-3.8-3.7a1 1 0 0 1 .55-1.7l5.2-.7Z" fill="currentColor"/></svg>
          </span>
          <span class="action-label">Today</span>
        </button>
        <button type="button" class="action" onclick={chooseEvening}>
          <span class="action-icon evening-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M19.5 14.4a8.5 8.5 0 0 1-10-10 1 1 0 0 0-1.4-1A9.5 9.5 0 1 0 20.4 15.7a1 1 0 0 0-.9-1.3Z" fill="currentColor"/></svg>
          </span>
          <span class="action-label">This Evening</span>
        </button>
        <button type="button" class="action" onclick={chooseTomorrow}>
          <span class="action-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M5 12a1 1 0 0 1 1-1h10.6l-3.3-3.3a1 1 0 1 1 1.4-1.4l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 1 1-1.4-1.4l3.3-3.3H6a1 1 0 0 1-1-1Z" fill="currentColor"/></svg>
          </span>
          <span class="action-label">Tomorrow</span>
        </button>
      </div>
      <div class="action-group cancel-group">
        <button type="button" class="action danger" onclick={chooseClear}>
          <span class="action-label">Clear</span>
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .star {
    flex-shrink: 0;
    padding: 6px 8px;
    color: var(--fg-faint);
    line-height: 1;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s, background 0.15s, transform 0.1s;
  }
  .star.sm svg { width: 16px; height: 16px; }
  .star:active { transform: scale(0.92); background: var(--tap); }
  .star.on { color: var(--accent); }
  .star.on.evening { color: #c4a3ff; }

  /* Action sheet — iOS bottom sheet pattern */
  .menu-backdrop {
    position: fixed;
    inset: 0;
    background: var(--material-overlay);
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
    z-index: 200;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 12px;
    padding-bottom: calc(12px + var(--safe-bottom));
    animation: fadeIn 0.18s ease-out;
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .action-sheet {
    width: 100%;
    max-width: 540px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    animation: sheetUp 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes sheetUp {
    from { transform: translateY(20%); opacity: 0; }
    to   { transform: translateY(0);   opacity: 1; }
  }
  .action-group {
    background: rgba(44, 44, 46, 0.92);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    backdrop-filter: saturate(180%) blur(20px);
    border-radius: 14px;
    overflow: hidden;
  }
  .action {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    padding: 14px 18px;
    font-size: 17px;
    color: var(--fg);
    border-bottom: 0.5px solid var(--separator);
  }
  .action:last-child { border-bottom: 0; }
  .action:active { background: var(--tap); }
  .action-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    color: var(--fg-dim);
  }
  .action-icon.star-icon { color: var(--accent); }
  .action-icon.evening-icon { color: #c4a3ff; }
  .action-label { flex: 1; }
  .cancel-group .action {
    justify-content: center;
    font-weight: 600;
    padding: 16px;
  }
  .action.danger { color: var(--priority-high); }
</style>
